import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CalendarBookingModal } from '@backrs_ui/grow-ui/src/components/CalendarBookingModal/CalendarBookingModal';
import {
  StudentType,
  useFetchUserProfileInfoQuery,
  UserProfileResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { STUDENT_PROGRAM_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { Box, SxProps, Theme } from '@mui/material';

import { useStudentDetails } from './hooks/useStudentDetails';
import { CoachDeepDivesSection } from './molecules/CoachDeepDivesSection';
import { CoachProfileSnapshot } from './molecules/CoachProfileSnapshot';
import { OwnProfileSidebar } from './molecules/OwnProfileSidebar';
import { OwnStudentSnapshot } from './molecules/OwnStudentSnapshot';
import { ProfileLinkedIn } from './molecules/ProfileLinkedIn';
import { PublicProfileHeader } from './molecules/PublicProfileHeader';
import { PublicProfileSnapshot } from './molecules/PublicProfileSnapshot';

const PROGRAM_LABELS = appStrings.modules.profile.public.programLabels;

/** Blank while loading so a Scholar never flashes as "Student"; undefined if it never loads. */
const programLabelFor = (
  studentType: StudentType | null | undefined,
  isLoading: boolean,
): string | undefined => {
  if (studentType !== undefined) return PROGRAM_LABELS[studentType ?? 'Student'];

  return isLoading ? '' : undefined;
};

type PublicProfileProps = Readonly<{
  profile: UserProfileResponse & {
    purposeVideoUrl?: string;
  };
  userId?: number;
  isCoachingEnabled?: boolean;
  isOwnProfile: boolean;
}>;

export const PublicProfile = ({
  profile,
  userId,
  isCoachingEnabled,
  isOwnProfile,
}: PublicProfileProps) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [contextProductId, setContextProductId] = useState<number | undefined>();
  const [bookingContext, setBookingContext] = useState<string | undefined>();

  const coaching = profile?.profile?.coaching;
  const isCoachView = !!coaching && !!isCoachingEnabled;
  const { isStudent } = useStudentDetails(profile);
  const isOwnStudentProfile = isOwnProfile && !isCoachView && isStudent;

  const { data: studentProgram, isLoading: isLoadingStudentProgram } = useFetchUserProfileInfoQuery(
    STUDENT_PROGRAM_FIELDS,
    { skip: !isOwnStudentProfile },
  );
  const programLabel = isOwnStudentProfile
    ? programLabelFor(studentProgram?.profile.studentType, isLoadingStudentProgram)
    : undefined;

  const hasCalendarLink = !!coaching?.consultation_calendar_url;
  const showCalendarModal =
    coaching?.consultation_calendar_url && coaching?.consultation_product_id && isBookingModalOpen;

  const handleScheduleConsultation = (ctxProductId?: number, ctxName?: string) => {
    setContextProductId(ctxProductId);
    setBookingContext(ctxName ?? 'Performance Coaching');
    setIsBookingModalOpen(true);
  };

  const handleBookingClose = () => {
    setIsBookingModalOpen(false);
    setContextProductId(undefined);
    setBookingContext(undefined);
  };

  const linkedIn = profile?.profile?.linkedInData && (
    <ProfileLinkedIn
      firstName={profile.profile?.publicProfile?.first_name || ''}
      linkedInData={profile?.profile.linkedInData}
    />
  );

  const renderContent = () => {
    if (isCoachView) {
      return (
        <>
          <CoachProfileSnapshot coaching={coaching} purposeVideoUrl={profile.purposeVideoUrl} />
          <CoachDeepDivesSection
            deepDives={coaching.deep_dives || []}
            onScheduleConsultation={hasCalendarLink ? handleScheduleConsultation : undefined}
          />
        </>
      );
    }

    if (!isOwnStudentProfile) {
      return (
        <>
          <PublicProfileSnapshot publicProfile={profile} />
          {linkedIn}
        </>
      );
    }

    return (
      <Box sx={columnsSx}>
        <Box sx={mainColumnSx}>
          <OwnStudentSnapshot
            profile={profile}
            sponsoringInstitutions={studentProgram?.profile.sponsoringInstitutions ?? []}
          />
          {linkedIn}
        </Box>

        <Box sx={sidebarColumnSx}>
          <OwnProfileSidebar
            resume={profile.profile.documentsResumes?.[0]}
            transcript={profile.profile.documentsTranscripts?.[0]}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={pageContainerSx}>
      {/** HEADER */}
      <PublicProfileHeader
        publicProfile={profile}
        isOwnProfile={isOwnProfile}
        onScheduleConsultation={hasCalendarLink ? handleScheduleConsultation : undefined}
        isCoachingEnabled={isCoachingEnabled}
        programLabel={programLabel}
      />

      <Box sx={contentContainerSx}>{renderContent()}</Box>

      {showCalendarModal && (
        <CalendarBookingModal
          open={isBookingModalOpen}
          userId={userId}
          productId={coaching.consultation_product_id}
          coachProductId={coaching.consultation_coach_product_id}
          calendarUrl={coaching.consultation_calendar_url}
          contextProductId={contextProductId}
          bookingContext={bookingContext}
          onClose={handleBookingClose}
        />
      )}
    </Box>
  );
};

const pageContainerSx: SxProps<Theme> = {
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
};

const contentContainerSx: SxProps<Theme> = {
  margin: '0 auto',
  maxWidth: '1216px',
  '@media (max-width: 900px) and (max-height: 504px)': {
    maxWidth: '100%',
    marginLeft: '0px',
  },
  width: '100%',
  // `clip` rather than `hidden`: a hidden overflow becomes the scroll container and breaks the sticky sidebar.
  overflowX: 'clip',
};

const columnsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { md: 'flex-start' },
  gap: backrs.spacing['spacing-3xl'],
  paddingRight: { md: backrs.spacing['spacing-4xl'] },
});

const mainColumnSx: SxProps<Theme> = { flex: 1, minWidth: 0 };

const sidebarColumnSx: SxProps<Theme> = ({ backrs }) => ({
  flex: { md: '0 0 300px' },
  minWidth: 0,
  marginX: { xs: backrs.spacing['spacing-xl'], sm: backrs.spacing['spacing-4xl'], md: 0 },
  position: { md: 'sticky' },
  top: { md: backrs.spacing['spacing-xl'] },
  // Offset leaves room for the app header above the page's scroll area.
  maxHeight: { md: 'calc(100svh - 160px)' },
  overflowY: { md: 'auto' },
  scrollbarWidth: 'thin',
});
